import Paper from '@mui/material/Paper';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup, { toggleButtonGroupClasses } from '@mui/material/ToggleButtonGroup';
import { styled } from '@mui/material/styles';
import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import usePrintLabelContext from 'common/hooks/usePrintLabelContext';
import lineItemEnum from 'enums/lineItem.enum';
import { DEFAULT_BULK_LABELS_FILTER } from 'store/slices/workQueueSlice';
import { filterBulkLabels } from 'store/thunks/workQueueThunks';
import { BULK_MODE, useBulkQueueMode } from './ChangeBulkModeContext';
import { useTransferFacilityContext } from './MultiTransferFacilityButton';

const CENTRAL_FILL_DEFAULT_FILTER = {
  isPrinted: false,
  lineItemStatus: lineItemEnum.PRODUCT_FULFILLMENT,
};

const DEFAULT_FILTER_BY_MODE = {
  [BULK_MODE.FULFILLMENT]: DEFAULT_BULK_LABELS_FILTER,
  [BULK_MODE.TRANSFER_FACILITY]: DEFAULT_BULK_LABELS_FILTER,
  [BULK_MODE.CENTRAL_FILL]: CENTRAL_FILL_DEFAULT_FILTER,
};

const StyledToggleButtonGroup = styled(ToggleButtonGroup)(({ theme }) => ({
  [`& .${toggleButtonGroupClasses.grouped}`]: {
    margin: theme.spacing(0.5),
    border: 0,
    borderRadius: theme.shape.borderRadius,
    [`&.${toggleButtonGroupClasses.disabled}`]: {
      border: 0,
    },
  },
  [`& .${toggleButtonGroupClasses.middleButton},& .${toggleButtonGroupClasses.lastButton}`]: {
    marginLeft: -1,
    borderLeft: '1px solid transparent',
  },
}));

const StyledToggleButton = styled(ToggleButton)(({ theme }) => ({
  textTransform: 'none',
  border: '1px solid #ccc',
  backgroundColor: '#f5f5f5',
  color: theme.palette.text.primary,
  '&:hover': {
    backgroundColor: '#e0e0e0',
  },
  '&.Mui-selected': {
    backgroundColor: theme.palette.primary.main,
    color: '#fff',
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
    },
  },
}));

const BulkLabelsChangeFacilityModeSwitch = () => {
  const { bulkQueueMode, setBulkQueueMode } = useBulkQueueMode();
  const { clearLabelItems } = usePrintLabelContext();
  const { clearTransferItems } = useTransferFacilityContext();
  const dispatch = useDispatch();
  const filtersByMode = useSelector(({ workQueue }) => workQueue.bulkLabels.filtersByMode);
  const previousModeRef = useRef(bulkQueueMode);

  // An exclusive ToggleButtonGroup reports null when the selected button is clicked again.
  const changeBulkFillMode = (_, mode) => mode && setBulkQueueMode(mode);

  useEffect(() => {
    const previousMode = previousModeRef.current;

    if (previousMode !== bulkQueueMode) {
      // Selections never carry across modes.
      clearLabelItems();
      if (previousMode === BULK_MODE.TRANSFER_FACILITY) {
        clearTransferItems();
      }

      // POM-5788: each mode gets back the filters it was left with; a first visit gets its default.
      const nextFilter = filtersByMode[bulkQueueMode] ?? DEFAULT_FILTER_BY_MODE[bulkQueueMode];
      if (nextFilter) {
        dispatch(filterBulkLabels({ filter: { ...nextFilter } }));
      }
    }

    previousModeRef.current = bulkQueueMode;
  }, [bulkQueueMode, filtersByMode, clearLabelItems, clearTransferItems, dispatch]);

  return (
    <div>
      <Paper
        elevation={0}
        sx={(theme) => ({
          display: 'flex',
          border: `1px solid ${theme.palette.divider}`,
          flexWrap: 'wrap',
        })}
      >
        <StyledToggleButtonGroup
          size='small'
          value={bulkQueueMode}
          exclusive
          onChange={changeBulkFillMode}
        >
          <StyledToggleButton value={BULK_MODE.FULFILLMENT} aria-label='centered'>
            Fulfillment Mode
          </StyledToggleButton>
          <StyledToggleButton value={BULK_MODE.TRANSFER_FACILITY} aria-label='left aligned'>
            Transfer Mode
          </StyledToggleButton>
          <StyledToggleButton value={BULK_MODE.CENTRAL_FILL} aria-label='central fill'>
            Central Fill Mode
          </StyledToggleButton>
        </StyledToggleButtonGroup>
      </Paper>
    </div>
  );
};

export default BulkLabelsChangeFacilityModeSwitch;
